import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { History, Loader2, Search } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { MOTIVOS } from "../ResolverTicketModal";

// Cor de cada motivo de encerramento. O mesmo vocabulário do modal de
// encerrar atendimento: a pessoa lê "Resolvido" aqui e "Resolvido" lá.
const CLASSE_DO_MOTIVO = {
	resolvido: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
	sem_retorno: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
	duplicado: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
	nao_era_atendimento: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

const CLASSE_EM_ANDAMENTO =
	"bg-blue-500/10 text-blue-600 dark:text-blue-400";

const CLASSE_ENCERRADO = "bg-slate-500/10 text-slate-600 dark:text-slate-400";

// Busca sem acento e sem diferenciar maiúscula, mas sem mexer no texto
// original: é o índice do texto normalizado que precisa bater com o pedaço
// que sai para a tela.
const semAcento = (texto) =>
	texto
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();

// Letra a letra, e não o texto inteiro de uma vez: um acento que já chega
// decomposto encolheria o texto normalizado e o destaque cairia no lugar
// errado.
const semAcentoMesmoTamanho = (texto) =>
	texto
		.split("")
		.map((letra) => semAcento(letra).charAt(0) || letra)
		.join("");

// Corta o texto em pedaços e marca onde o termo aparece.
const destacar = (texto, termo) => {
	const alvo = semAcento(termo.trim());
	if (!alvo) return [{ texto, marcado: false }];

	const base = semAcentoMesmoTamanho(texto);
	const pedacos = [];
	let cursor = 0;
	let achado = base.indexOf(alvo);

	while (achado !== -1) {
		if (achado > cursor) {
			pedacos.push({ texto: texto.slice(cursor, achado), marcado: false });
		}
		pedacos.push({
			texto: texto.slice(achado, achado + alvo.length),
			marcado: true,
		});
		cursor = achado + alvo.length;
		achado = base.indexOf(alvo, cursor);
	}

	if (cursor < texto.length) {
		pedacos.push({ texto: texto.slice(cursor), marcado: false });
	}

	return pedacos;
};

// O que a empresa já atendeu com esta pessoa, com o motivo e a observação de
// cada encerramento.
//
// Fica ao lado da busca da conversa porque é ali que surge a pergunta "isso
// já aconteceu antes?". A busca da conversa acha o que foi dito; esta acha o
// que a equipe anotou ao encerrar.
const HistoricoAtendimentos = ({ ticketId }) => {
	const navigate = useNavigate();

	const [aberto, setAberto] = useState(false);
	const [busca, setBusca] = useState("");
	const [buscaAplicada, setBuscaAplicada] = useState("");
	const [escopo, setEscopo] = useState("contato");
	const [motivo, setMotivo] = useState("todos");
	const [pagina, setPagina] = useState(1);
	const [itens, setItens] = useState([]);
	const [total, setTotal] = useState(0);
	const [temMais, setTemMais] = useState(false);
	const [carregando, setCarregando] = useState(false);
	const [carregandoLista, setCarregandoLista] = useState(false);
	const [contato, setContato] = useState(null);

	// Número da requisição: a busca antiga pode voltar depois da nova e
	// sobrescrever a lista com um resultado que ninguém está olhando.
	const requisicao = useRef(0);

	const carregar = useCallback(
		async (paginaNova, substituir) => {
			const atual = requisicao.current + 1;
			requisicao.current = atual;

			setCarregando(true);
			// Só a troca de filtro ou de escopo esconde a lista: ao carregar
			// mais páginas a lista anterior fica na tela.
			if (substituir) setCarregandoLista(true);
			try {
				const { data } = await api.get("/historico-atendimentos", {
					params: {
						ticketId,
						escopo,
						busca: buscaAplicada || undefined,
						motivo,
						pageNumber: paginaNova,
					},
				});

				// Resposta atrasada de uma busca que já foi trocada.
				if (requisicao.current !== atual) return;

				const lista = data?.tickets || [];
				setItens((atualLista) =>
					substituir ? lista : [...atualLista, ...lista],
				);
				setTotal(Number(data?.count) || 0);
				setTemMais(Boolean(data?.hasMore));
				setContato(data?.contato || null);
				setPagina(paginaNova);
			} catch (err) {
				if (requisicao.current !== atual) return;
				toastError(err);
			} finally {
				if (requisicao.current === atual) {
					setCarregando(false);
					setCarregandoLista(false);
				}
			}
		},
		[ticketId, escopo, buscaAplicada, motivo],
	);

	// Espera a pessoa parar de digitar: uma requisição por tecla é fila demais
	// e a lista pisca sem parar.
	useEffect(() => {
		const tempo = setTimeout(() => setBuscaAplicada(busca.trim()), 350);
		return () => clearTimeout(tempo);
	}, [busca]);

	// Mudou a busca, o escopo ou o motivo: volta para a primeira página e
	// troca a lista inteira.
	useEffect(() => {
		if (!aberto) return;
		carregar(1, true);
	}, [aberto, carregar]);

	const abrirAtendimento = (id) => {
		setAberto(false);
		navigate(`/tickets/${id}`);
	};

	const rotuloDoMotivo = (item) => {
		if (item.status !== "closed") {
			return { texto: "Em andamento", classe: CLASSE_EM_ANDAMENTO };
		}

		const motivoConhecido = MOTIVOS.find((m) => m.chave === item.closeReason);
		if (!motivoConhecido) {
			return { texto: "Encerrado", classe: CLASSE_ENCERRADO };
		}

		return {
			texto: motivoConhecido.rotulo,
			classe: CLASSE_DO_MOTIVO[motivoConhecido.chave],
		};
	};

	return (
		<Dialog open={aberto} onOpenChange={setAberto}>
			<DialogTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="h-8 gap-1.5 text-xs"
					aria-label="Histórico de atendimentos"
					title="Histórico de atendimentos"
				>
					<History className="h-3.5 w-3.5" />
					<span className="hidden sm:inline">Histórico</span>
				</Button>
			</DialogTrigger>

			<DialogContent className="flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0">
				<DialogHeader className="border-b px-4 py-3">
					<DialogTitle>Histórico de atendimentos</DialogTitle>
					<DialogDescription>
						{escopo === "contato"
							? `${contato?.name || "Contato"} · ${total} atendimento(s)`
							: "Todos os contatos que você pode ver"}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-2 border-b px-4 py-3">
					<div className="flex flex-wrap items-center gap-2">
						<div className="relative min-w-[220px] flex-1">
							<Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={busca}
								onChange={(e) => setBusca(e.target.value)}
								placeholder="Buscar por contato, nº do atendimento ou observação…"
								aria-label="Buscar por contato, nº do atendimento ou observação…"
								className="h-8 pl-7 text-xs"
							/>
						</div>

						{/* "Todos os contatos" acha o mesmo problema em outros
						    clientes ("VPN", "certificado"), dentro do que o cargo
						    da pessoa deixa ver — o servidor aplica o recorte. */}
						<div
							role="group"
							aria-label="Abrangência do histórico"
							className="flex items-center gap-1 rounded-lg border p-0.5"
						>
							<Button
								variant={escopo === "contato" ? "secondary" : "ghost"}
								size="sm"
								className="h-7 text-xs"
								aria-pressed={escopo === "contato"}
								onClick={() => setEscopo("contato")}
							>
								Este contato
							</Button>
							<Button
								variant={escopo === "todos" ? "secondary" : "ghost"}
								size="sm"
								className="h-7 text-xs"
								aria-pressed={escopo === "todos"}
								onClick={() => setEscopo("todos")}
							>
								Todos os contatos
							</Button>
						</div>
					</div>

					<div className="flex flex-wrap items-center gap-1.5">
						{[
							{ chave: "todos", rotulo: "Todos" },
							...MOTIVOS,
						].map((item) => (
							<button
								key={item.chave}
								type="button"
								aria-pressed={motivo === item.chave}
								onClick={() => setMotivo(item.chave)}
								className={cn(
									"rounded-full border px-2.5 py-0.5 text-xs transition-colors",
									motivo === item.chave
										? "border-primary bg-primary/10 font-medium"
										: "border-border text-muted-foreground hover:bg-muted",
								)}
							>
								{item.rotulo}
							</button>
						))}
					</div>
				</div>

				<div className="flex-1 overflow-auto">
					{carregandoLista ? (
						<div className="flex justify-center py-10">
							<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
						</div>
					) : itens.length === 0 ? (
						<div className="py-10 text-center text-sm text-muted-foreground">
							{buscaAplicada
								? `Nada encontrado para “${buscaAplicada}”.`
								: "Nenhum atendimento encontrado."}
						</div>
					) : (
						<table className="w-full text-sm">
							<thead className="sticky top-0 z-10 bg-background text-left text-xs text-muted-foreground">
								<tr className="border-b">
									<th className="px-4 py-2 font-medium">Atendimento</th>
									<th className="px-4 py-2 font-medium">Data</th>
									<th className="px-4 py-2 font-medium">Atendente / Fila</th>
									<th className="px-4 py-2 font-medium">Motivo</th>
									<th className="px-4 py-2 font-medium">
										Observação do encerramento
									</th>
								</tr>
							</thead>
							<tbody>
								{itens.map((item) => {
									const emAndamento = item.status !== "closed";
									const motivoItem = rotuloDoMotivo(item);
									const ehATual = Number(item.id) === Number(ticketId);

									return (
										<tr key={item.id} className="border-b align-top last:border-0">
											<td className="px-4 py-2">
												{escopo === "todos" && (
													<p className="max-w-[200px] truncate font-medium">
														{item.contact?.name || "Sem contato"}
													</p>
												)}
												<div className="flex flex-wrap items-center gap-2">
													<span className="text-xs tabular-nums">#{item.id}</span>
													{item.whatsapp?.name && (
														<span className="max-w-[160px] truncate text-[11px] text-muted-foreground">
															{item.whatsapp.name}
														</span>
													)}
													{ehATual ? (
														<span className="text-[11px] text-muted-foreground">
															atual
														</span>
													) : (
														<Button
															variant="link"
															className="h-auto p-0 text-xs"
															onClick={() => abrirAtendimento(item.id)}
														>
															Abrir
														</Button>
													)}
												</div>
											</td>

											<td className="whitespace-nowrap px-4 py-2 text-xs">
												{item.closedAt ? (
													format(parseISO(item.closedAt), "dd/MM/yyyy HH:mm")
												) : item.createdAt ? (
													<span className="text-muted-foreground">
														aberto em{" "}
														{format(parseISO(item.createdAt), "dd/MM/yyyy HH:mm")}
													</span>
												) : (
													"—"
												)}
											</td>

											<td className="px-4 py-2 text-xs">
												<div className="font-medium">{item.user?.name || "—"}</div>
												{item.queue?.name && (
													<div className="text-[11px] text-muted-foreground">
														{item.queue.name}
													</div>
												)}
											</td>

											<td className="px-4 py-2">
												<span
													className={cn(
														"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
														motivoItem.classe,
													)}
												>
													{motivoItem.texto}
												</span>
											</td>

											<td className="px-4 py-2 text-xs">
												{item.closeNote ? (
													<p className="whitespace-pre-line">
														{destacar(item.closeNote, buscaAplicada).map(
															(pedaco, indice) =>
																pedaco.marcado ? (
																	<mark
																		key={indice}
																		className="rounded bg-amber-300/40 px-0.5 text-inherit"
																	>
																		{pedaco.texto}
																	</mark>
																) : (
																	<span key={indice}>{pedaco.texto}</span>
																),
														)}
													</p>
												) : (
													<span className="italic text-muted-foreground">
														{emAndamento
															? "Atendimento em andamento"
															: "Sem observação"}
													</span>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					)}
				</div>

				<div className="flex items-center gap-3 border-t px-4 py-2 text-xs text-muted-foreground">
					<span>Mostrando {itens.length} de {total}</span>

					{temMais && (
						<Button
							variant="outline"
							size="sm"
							className="h-7 text-xs"
							disabled={carregando}
							onClick={() => carregar(pagina + 1, false)}
						>
							{carregando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
							Carregar mais
						</Button>
					)}

					<span className="ml-auto">
						As observações ficam só para a equipe. O cliente não vê.
					</span>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default HistoricoAtendimentos;
